import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Stack,
    Box,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableRow,
    TableHead,
    Divider,
    Popper,
    Tooltip,
    TextField,
} from "@mui/material";
import { FilterAlt } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LoadingBox } from "../../components/ui/Box";
import CircularProgress from "@mui/material/CircularProgress";
import {
    GetGeneratedReport,
    GetTransactionReport,
    GetVepayFeeReport,
    GetMarketplaceOverview,
    GetDailySummary,
    GetTransactionBreakdown
} from "../../services/reports.services";
import { ContainedButton, LightBlueContainedButton, TextButton } from "../../components/ui/Buttons";
import { NoBorderTextArea } from "../../components/ui/TextBox";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import solidGauge from "highcharts/modules/solid-gauge";
import hcMore from 'highcharts/highcharts-more';
import { StyledTableContainer, StyledTableCellBody, StyledTableCellHeader } from "../../components/ui/Table";
import moment from "moment";

hcMore(Highcharts);
solidGauge(Highcharts);

// ─── Helper: Reusable Section Card ───────────────────────────────────────────
const SectionBox = ({ children, sx }) => (
    <Box
        sx={{
            p: 3,
            backgroundColor: "#FFFFFF",
            boxShadow: "0px 0px 4px rgba(3, 40, 120, 0.18)",
            borderRadius: "2px",
            mb: 3,
            ...sx
        }}
    >
        {children}
    </Box>
);

const SectionTitle = ({ children }) => (
    <Typography variant="h4" sx={{ mb: 2 }}>{children}</Typography>
);

const SubSectionTitle = ({ children }) => (
    <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 1 }}><b>{children}</b></Typography>
);

// ─── Helper: Build a pie chart config ────────────────────────────────────────
const buildPieOptions = (title, data, colors) => ({
    title: { text: title, align: "left" },
    chart: { type: 'pie' },
    colors: colors || ['#0096DA', '#1abc9c', '#e74c3c', '#f39c12', '#9b59b6', '#2ecc71', '#e67e22', '#3498db'],
    tooltip: { pointFormat: "{series.name}: <b>" + "$" + "{point.y:.2f}</b> ({point.percentage:.1f}%)" },
    plotOptions: { pie: { allowPointSelect: true, cursor: 'pointer', dataLabels: { enabled: true, format: "<b>{point.name}</b>: " + "$" + "{point.y:.2f}" } } },
    series: [{ name: title, data }],
    credits: { enabled: false },
});

// ─── Helper: Build a donut chart config ──────────────────────────────────────
const buildDonutOptions = (title, data, colors) => ({
    title: { text: title, align: "left" },
    chart: { type: 'pie' },
    colors: colors || ['#0096DA', '#1abc9c', '#e74c3c', '#f39c12', '#9b59b6', '#2ecc71', '#e67e22', '#3498db'],
    tooltip: { pointFormat: "{series.name}: <b>" + "$" + "{point.y:.2f}</b> ({point.percentage:.1f}%)" },
    plotOptions: { pie: { allowPointSelect: true, cursor: 'pointer', innerSize: '50%', dataLabels: { enabled: true, format: "<b>{point.name}</b>: " + "$" + "{point.y:.2f}" } } },
    series: [{ name: title, data }],
    credits: { enabled: false },
});

// ─── Helper: Build a column chart config ─────────────────────────────────────
const buildColumnOptions = (title, categories, seriesArr, colors) => ({
    title: { text: title || "", align: "left" },
    chart: { type: 'column' },
    colors: colors || ["#0096DA", "#1abc9c", "#e74c3c"],
    xAxis: { categories, crosshair: true },
    yAxis: { title: { text: "Amount (USD)" } },
    tooltip: { pointFormat: "{series.name}: <b>" + "$" + "{point.y:.2f}</b><br/>" },
    series: seriesArr,
    credits: { enabled: false },
});

// ─── Helper: Empty Table Row ─────────────────────────────────────────────────
const EmptyRow = ({ colSpan, message }) => (
    <TableRow>
        <StyledTableCellBody colSpan={colSpan} align="center" sx={{ padding: "5%" }}>
            <Typography variant="body1">{message || "No data found"}</Typography>
        </StyledTableCellBody>
    </TableRow>
);


export default function GenerateReport() {
    const [isLoading, setIsLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [generateReport, setGenerateReport] = useState(null);
    const [reportDescription, setReportDescription] = useState('');

    const factorStart = localStorage.getItem("factorStartDate");
    const reportStartDate = factorStart ? moment(factorStart).toDate() : moment().subtract(30, 'days').toDate();
    const reportEndDate = moment().toDate();

    const [startDate, setStartDate] = useState(reportStartDate);
    const [endDate, setEndDate] = useState(reportEndDate);
    const [startDateError, setStartDateError] = useState(false);
    const [endDateError, setEndDateError] = useState(false);
    const [value, setValue] = useState([reportStartDate, reportEndDate]);
    const [filter, setFilter] = useState(null);

    const hasRun = useRef(false);

    const handleFilter = () => {
        setValue([startDate, endDate]);
        handleCloseMP();
    };

    const handlerFilterReset = () => {
        setStartDate(reportStartDate);
        setEndDate(reportEndDate);
        setValue([reportStartDate, reportEndDate]);
        handleCloseMP();
    };

    const handleClickMP = (event) => setFilter(event.currentTarget);
    const handleCloseMP = () => setFilter(null);
    const openMP = Boolean(filter);
    const idMP = openMP ? "simple-popover" : undefined;

    // ─── Overview States ─────────────────────────────────────────────────────
    const [amazonOverview, setAmazonOverview] = useState({ totalProductSales: 0, totalProductCharges: 0, totalNetSales: 0, totalRefunds: 0, returnPercentage: 0, marketplaceCharges: 0 });
    const [vepayOverview, setVepayOverview] = useState({ totalFundedAmount: 0, receivableBalance: 0, vepayFeePercentage: 0, vepayFee: 0 });

    // ─── Sales States ────────────────────────────────────────────────────────
    const [salesAnalysisData, setSalesAnalysisData] = useState({ dates: [], totalSales: [], productSales: [] });
    const [topProducts, setTopProducts] = useState([]);
    const [topSalesBySku, setTopSalesBySku] = useState([]);
    const [topSalesByState, setTopSalesByState] = useState([]);

    // ─── Return States ───────────────────────────────────────────────────────
    const [returnAnalysisData, setReturnAnalysisData] = useState({ dates: [], totalNetSales: [], totalRefunds: [] });
    const [topReturnsByProduct, setTopReturnsByProduct] = useState([]);
    const [topReturnsBySku, setTopReturnsBySku] = useState([]);
    const [topReturnsByState, setTopReturnsByState] = useState([]);

    // ─── Amazon Charges States ───────────────────────────────────────────────
    const [amazonChargesAnalysis, setAmazonChargesAnalysis] = useState({ dates: [], totalNetSales: [], totalAmazonCharges: [] });
    const [amazonChargesBreakdown, setAmazonChargesBreakdown] = useState([]);
    const [productChargesOnSales, setProductChargesOnSales] = useState([]);

    // ─── VePay Funding States ────────────────────────────────────────────────
    const [vepayFundingData, setVepayFundingData] = useState({ dates: [], funded: [], vepayFee: [] });

    // ─── Monthly Summary States ──────────────────────────────────────────────
    const [monthlySummary, setMonthlySummary] = useState({ months: [], productSales: [], netSales: [] });
    const [totalMonthlyPromoRebates, setTotalMonthlyPromoRebates] = useState(0);
    const [totalMonthlyFbaFees, setTotalMonthlyFbaFees] = useState(0);

    useEffect(() => {
        const fetchOverviews = async () => {
            try {
                setDataLoading(true);

                const transactionData = await GetTransactionBreakdown("Transactions", value[0], value[1]);
                const vepayFeesData = await GetTransactionBreakdown("Net Sales - Vepay Fee", value[0], value[1]);
                const overviewDataResponse = await GetTransactionBreakdown("Marketplace Payment", value[0], value[1]);
                
                const todayDate = moment(value[1]).format("YYYY-MM-DD");
                const dailySummaryData = await GetDailySummary(todayDate);
                
                // Filter dailySummaryData by date range
                if (dailySummaryData && typeof dailySummaryData === 'object') {
                    Object.keys(dailySummaryData).forEach(key => {
                        const dateNum = moment(key).valueOf();
                        if (dateNum < moment(value[0]).startOf('day').valueOf() || dateNum > moment(value[1]).endOf('day').valueOf()) {
                             delete dailySummaryData[key];
                        }
                    });
                }

                const transactions = Array.isArray(transactionData) ? transactionData : [];
                const vepayFees = Array.isArray(vepayFeesData) ? vepayFeesData : [];
                // The third is now an array from Marketplace Payment instead of a single object
                const overview = Array.isArray(overviewDataResponse) ? overviewDataResponse[0] || {} : (overviewDataResponse || {});

                // ─── 1. Amazon Overview & VePay Overview ──────────────────
                let totalNetSales = 0, sumProductSales = 0, totalRefunds = 0, sumPlatformCharges = 0, totalFundedAmount = 0;
                
                if (dailySummaryData && typeof dailySummaryData === 'object') {
                    Object.values(dailySummaryData).forEach(d => {
                        totalNetSales += (d["Net Sales"] || []).reduce((a, v) => a + (v || 0), 0);
                        sumProductSales += (d["Product charges"] || []).reduce((a, v) => a + Math.abs(v || 0), 0);
                        totalRefunds += (d["Refunds"] || []).reduce((a, v) => a + Math.abs(v || 0), 0);
                        sumPlatformCharges += (d["Selling platform"] || []).reduce((a, v) => a + Math.abs(v || 0), 0);
                        totalFundedAmount += (d["Carry Forward"] || []).reduce((a, v) => a + (v || 0), 0);
                    });
                }
                const returnPct = totalNetSales > 0 ? (totalRefunds / totalNetSales) * 100 : 0;

                setAmazonOverview({
                    totalProductSales: sumProductSales,
                    totalProductCharges: sumPlatformCharges,
                    totalNetSales,
                    totalRefunds,
                    returnPercentage: returnPct,
                    marketplaceCharges: sumPlatformCharges
                });

                const feePercentage = vepayFees.length > 0 ? (vepayFees[0].revenuePercentage || 0) : 0;
                const totalVepayFee = vepayFees.reduce((sum, f) => sum + Math.abs(f.amount || 0), 0);

                setVepayOverview({
                    totalFundedAmount: totalFundedAmount,
                    receivableBalance: 0,
                    vepayFeePercentage: feePercentage,
                    vepayFee: totalVepayFee
                });

                // ─── 3. Top Products (for sales table) ──────────────────
                const groupedByDesc = {};
                transactions.forEach(tx => {
                    const desc = tx.description || "Unknown";
                    if (!groupedByDesc[desc]) groupedByDesc[desc] = { description: desc, totalProductSales: 0, totalNetSales: 0, totalRefunds: 0, totalCharges: 0 };
                    
                    groupedByDesc[desc].totalProductSales += (tx.amount2 || Math.max(0, tx.amount || 0));
                    groupedByDesc[desc].totalNetSales += (tx.amount2 ? tx.amount2 + (tx.amount || 0) : Math.max(0, tx.amount || 0));
                    groupedByDesc[desc].totalCharges += (tx.amount < 0 ? Math.abs(tx.amount) : 0);
                });
                console.log(transactions)
                const productsList = Object.values(groupedByDesc).sort((a, b) => b.totalProductSales - a.totalProductSales).slice(0, 10);
                setTopProducts(productsList);

                // ─── 4. Top Sales by SKU (pie) ──────────────────────────
                const groupedBySku = {};
                transactions.forEach(tx => {
                    const sku = tx.ref1?.replace("AMAZON+", "") || "Unknown SKU";
                    if (!groupedBySku[sku]) groupedBySku[sku] = 0;
                    groupedBySku[sku] += (tx.amount2 || Math.max(0, tx.amount || 0));
                });
                const skuSalesData = Object.entries(groupedBySku)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 8)
                    .map(([name, y]) => ({ name, y: Number(y.toFixed(2)) }));
                setTopSalesBySku(skuSalesData);

                // ─── 5. Top Sales by State (donut) ──────────────────────
                const groupedByMarketplace = {};
                transactions.forEach(tx => {
                    const state = tx.ref2 || "Unknown";
                    if (!groupedByMarketplace[state]) groupedByMarketplace[state] = 0;
                    groupedByMarketplace[state] += (tx.amount2 || Math.max(0, tx.amount || 0));
                });
                const stateSalesData = Object.entries(groupedByMarketplace)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 8)
                    .map(([name, y]) => ({ name, y: Number(y.toFixed(2)) }));
                setTopSalesByState(stateSalesData);

                // ─── 6. Return Analysis / Top Returns ────────────────────
                const groupedReturns = {};
                transactions.forEach(tx => {
                    const desc = tx.description || "Unknown";
                    const isRefund = desc.toLowerCase().includes('refund') || desc.toLowerCase().includes('return');
                    if (isRefund || (tx.amount < 0 && desc === "Refund")) {
                        if (!groupedReturns[desc]) groupedReturns[desc] = { description: desc, totalRefunds: 0 };
                        groupedReturns[desc].totalRefunds += Math.abs(tx.amount || 0);
                    }
                });
                const returnsByProduct = Object.values(groupedReturns).filter(r => r.totalRefunds > 0).sort((a, b) => b.totalRefunds - a.totalRefunds).slice(0, 10);
                setTopReturnsByProduct(returnsByProduct);

                // Top Returns by SKU (pie)
                const returnsBySku = {};
                transactions.forEach(tx => {
                    const desc = (tx.description || '').toLowerCase();
                    const isRefund = desc.includes('refund') || desc.includes('return');
                    if (isRefund || (tx.amount < 0 && desc === "refund")) {
                        const sku = tx.ref1?.replace("AMAZON+", "") || "Unknown SKU";
                        if (!returnsBySku[sku]) returnsBySku[sku] = 0;
                        returnsBySku[sku] += Math.abs(tx.amount || 0);
                    }
                });
                const skuReturnsData = Object.entries(returnsBySku)
                    .filter(([, v]) => v > 0)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 8)
                    .map(([name, y]) => ({ name, y: Number(y.toFixed(2)) }));
                setTopReturnsBySku(skuReturnsData);

                // Top Returns by State (donut)
                const returnsByState = {};
                transactions.forEach(tx => {
                    const desc = (tx.description || '').toLowerCase();
                    const isRefund = desc.includes('refund') || desc.includes('return');
                    if (isRefund || (tx.amount < 0 && desc === "refund")) {
                        const state = tx.ref2 || "Unknown";
                        if (!returnsByState[state]) returnsByState[state] = 0;
                        returnsByState[state] += Math.abs(tx.amount || 0);
                    }
                });
                const stateReturnsData = Object.entries(returnsByState)
                    .filter(([, v]) => v > 0)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 8)
                    .map(([name, y]) => ({ name, y: Number(y.toFixed(2)) }));
                setTopReturnsByState(stateReturnsData);

                // ─── 7. Amazon Charges Breakdown (pie) ───────────────────
                const groupedCharges = {};
                transactions.forEach(tx => {
                    if (tx.amount < 0 && !(tx.description || '').toLowerCase().includes('refund')) {
                        const desc = tx.description || "Other Fee";
                        groupedCharges[desc] = (groupedCharges[desc] || 0) + Math.abs(tx.amount);
                    }
                });
                const chargesBreakdown = Object.entries(groupedCharges)
                    .map(([name, y]) => ({ name, y: Number(y.toFixed(2)) }))
                    .sort((a, b) => b.y - a.y)
                    .slice(0, 6);
                setAmazonChargesBreakdown(chargesBreakdown);

                // Product Charges on Sales (table)
                const chargesPerProduct = Object.values(groupedByDesc).map(p => ({
                    description: p.description,
                    totalProductSales: p.totalProductSales,
                    totalCharges: p.totalCharges,
                    chargesPercent: p.totalProductSales > 0 ? (p.totalCharges / p.totalProductSales) * 100 : 0
                })).sort((a, b) => b.totalCharges - a.totalCharges).slice(0, 10);
                setProductChargesOnSales(chargesPerProduct);

                // ─── 8-12. Process Daily Summary for all Column Charts ──
                if (dailySummaryData && typeof dailySummaryData === 'object') {
                    const sortedDates = Object.keys(dailySummaryData).sort((a, b) => new Date(a) - new Date(b));

                    // Sales Analysis
                    const sDates = [], sTotalSales = [], sProductSales = [];
                    // Return Analysis
                    const rDates = [], rNetSales = [], rRefunds = [];
                    // Amazon Charges Analysis
                    const aDates = [], aNetSales = [], aCharges = [];
                    // VePay Funding
                    const vDates = [], vFunded = [], vFee = [];
                    // Monthly buckets
                    const monthlyBuckets = {};

                    sortedDates.forEach(date => {
                        const d = dailySummaryData[date];
                        const formattedDate = moment(date).format("MMM D");
                        const monthKey = moment(date).format("MMM YYYY");

                        const netSales = (d["Net Sales"] || []).reduce((a, v) => a + (v || 0), 0);
                        const productCharges = (d["Product charges"] || []).reduce((a, v) => a + Math.abs(v || 0), 0);
                        const refunds = (d["Refunds"] || []).reduce((a, v) => a + Math.abs(v || 0), 0);
                        const sellingPlatform = (d["Selling platform"] || []).reduce((a, v) => a + Math.abs(v || 0), 0);
                        const vepayFeeDaily = (d["VePay Fee"] || []).reduce((a, v) => a + Math.abs(v || 0), 0);
                        const salesAdjustments = (d["Sales Adjustments"] || []).reduce((a, v) => a + (v || 0), 0);

                        // Sales Analysis
                        sDates.push(formattedDate);
                        sTotalSales.push(Number(netSales.toFixed(2)));
                        sProductSales.push(Number(productCharges.toFixed(2)));

                        // Return Analysis
                        rDates.push(formattedDate);
                        rNetSales.push(Number(netSales.toFixed(2)));
                        rRefunds.push(Number(refunds.toFixed(2)));

                        // Amazon Charges
                        aDates.push(formattedDate);
                        aNetSales.push(Number(netSales.toFixed(2)));
                        aCharges.push(Number(sellingPlatform.toFixed(2)));

                        // VePay Funding
                        vDates.push(formattedDate);
                        const carryForward = (d["Carry Forward"] || []).reduce((a, v) => a + (v || 0), 0);
                        vFunded.push(Number(carryForward.toFixed(2)));
                        vFee.push(Number(vepayFeeDaily.toFixed(2)));

                        // Monthly Summary
                        if (!monthlyBuckets[monthKey]) monthlyBuckets[monthKey] = { productSales: 0, netSales: 0, promoRebates: 0, fbaFees: 0 };
                        monthlyBuckets[monthKey].productSales += productCharges;
                        monthlyBuckets[monthKey].netSales += netSales;
                        monthlyBuckets[monthKey].promoRebates += Math.abs(salesAdjustments);
                        monthlyBuckets[monthKey].fbaFees += sellingPlatform;
                    });

                    setSalesAnalysisData({ dates: sDates, totalSales: sTotalSales, productSales: sProductSales });
                    setReturnAnalysisData({ dates: rDates, totalNetSales: rNetSales, totalRefunds: rRefunds });
                    setAmazonChargesAnalysis({ dates: aDates, totalNetSales: aNetSales, totalAmazonCharges: aCharges });
                    setVepayFundingData({ dates: vDates, funded: vFunded, vepayFee: vFee });

                    // Monthly Summary
                    const monthKeys = Object.keys(monthlyBuckets).sort((a, b) => new Date(a) - new Date(b));
                    const mMonths = [], mProductSales = [], mNetSales = [];
                    let promoTotal = 0, fbaTotal = 0;
                    monthKeys.forEach(mk => {
                        mMonths.push(mk);
                        mProductSales.push(Number(monthlyBuckets[mk].productSales.toFixed(2)));
                        mNetSales.push(Number(monthlyBuckets[mk].netSales.toFixed(2)));
                        promoTotal += monthlyBuckets[mk].promoRebates;
                        fbaTotal += monthlyBuckets[mk].fbaFees;
                    });
                    setMonthlySummary({ months: mMonths, productSales: mProductSales, netSales: mNetSales });
                    setTotalMonthlyPromoRebates(promoTotal);
                    setTotalMonthlyFbaFees(fbaTotal);
                }

            } catch (error) {
                console.error("Error fetching overview data:", error);
            } finally {
                setDataLoading(false);
            }
        };

        fetchOverviews();
    }, [value]);

    const handleSubmit = () => {
        const value = { userQuery: reportDescription };
        try {
            setIsLoading(true);
            GetGeneratedReport(value).then((data) => {
                setIsLoading(false);
                setGenerateReport(data.result);
            }).catch(() => setIsLoading(false));
        } catch (error) {
            console.error('Error', error);
            setIsLoading(false);
        }
    }

    // ─── AI Chart ────────────────────────────────────────────────────────────
    const aiChartOptions = {
        title: { text: "Report Overview", align: "left" },
        chart: { type: 'column' },
        plotOptions: { column: { stacking: "normal" } },
        colors: ["#0096DA"],
        xAxis: { categories: generateReport?.data ? generateReport.data.map((item) => Object.keys(item)[0]) : [], crosshair: true },
        yAxis: { title: { text: "Amount" } },
        tooltip: { pointFormat: "{series.name}: <b>" + "$" + "{point.y:.2f}</b><br/>" },
        series: [{ name: "Generated Data", data: generateReport?.data ? generateReport.data.map((item) => Object.values(item)[0]) : [] }],
        credits: { enabled: false },
    };

    // ─── RENDER ──────────────────────────────────────────────────────────────
    if (dataLoading) {
        return (
            <LoadingBox>
                <CircularProgress />
            </LoadingBox>
        );
    }

    return (
        <div>
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} sx={{ mb: 2, mt: -2 }}>
                <Typography variant="body2">
                    Time period {moment(value[0]).format("MMM D, y")} -&nbsp;{moment(value[1]).format("MMM D, y")}
                </Typography>
                <Tooltip title="Filter Report">
                    <TextButton variant="text" onClick={handleClickMP}>
                        <FilterAlt />
                    </TextButton>
                </Tooltip>
            </Stack>

            <Popper 
                id={idMP}
                open={openMP}
                anchorEl={filter}
                onClose={handleCloseMP}
                placement={'bottom-end'}
                sx={{ p: 2, bgcolor: 'background.paper', width: "25%", boxShadow: "0px 0px 4px rgba(3, 40, 120, 0.18)", zIndex: 1000 }}
            >
                <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                    <Typography sx={{ p: 2 }} variant="h5"><b>Filter Report</b></Typography>
                    <CloseIcon onClick={handleCloseMP} sx={{ cursor: 'pointer' }} />
                </Stack>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack sx={{ width: "100%", mt: 2 }} spacing={2}>
                        <DatePicker
                            openTo="day"
                            views={["year", "month", "day"]}
                            label="Start"
                            value={startDate}
                            maxDate={endDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            renderInput={(params) => {
                                setStartDateError(params.error);
                                return <TextField {...params} helperText={null} />;
                            }}
                        />
                        <DatePicker
                            openTo="day"
                            views={["year", "month", "day"]}
                            label="End"
                            value={endDate}
                            minDate={startDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            renderInput={(params) => {
                                setEndDateError(params.error);
                                return <TextField {...params} helperText={null} />;
                            }}
                        />
                    </Stack>
                </LocalizationProvider>
                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                    {startDateError === false && endDateError === false ? (
                        <LightBlueContainedButton fullWidth variant="outlined" onClick={handleFilter} size="large">Filter</LightBlueContainedButton>
                    ) : (
                        <LightBlueContainedButton fullWidth variant="outlined" disabled>Apply</LightBlueContainedButton>
                    )}
                    <LightBlueContainedButton fullWidth variant="contained" onClick={handlerFilterReset} size="large">Reset</LightBlueContainedButton>
                </Stack>
            </Popper>

            {/* ═══════════════════════ Generate Report (AI) ═══════════════════ */}
            {/* <SectionBox>
                <SectionTitle>GENERATE REPORT</SectionTitle>
                <Stack spacing={2}>
                    <Typography variant="h5">What Report do you want? Type your expectation:</Typography>
                    <NoBorderTextArea
                        fullWidth multiline rows={6}
                        value={reportDescription}
                        onChange={(e) => setReportDescription(e.target.value)}
                        placeholder="my monthly wallet balance trend...."
                    />
                    <ContainedButton variant="contained" sx={{ width: '20%' }} disabled onClick={handleSubmit}>Generate</ContainedButton>
                </Stack>
                {isLoading ? (
                    <LoadingBox><CircularProgress /></LoadingBox>
                ) : (
                    generateReport?.data && generateReport.data.length > 0 ? (
                        <Box sx={{ mt: 4 }}><HighchartsReact options={aiChartOptions} highcharts={Highcharts} /></Box>
                    ) : null
                )}
            </SectionBox> */}

            {/* ═══════════════════════ Overview Cards ═════════════════════════ */}
            <SectionBox>
                <SectionTitle>OVERVIEW DASHBOARD</SectionTitle>
                <Grid container spacing={3}>
                    {/* Amazon Overview */}
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h5" color="primary" gutterBottom><b>Amazon Overview</b></Typography>
                                <Stack spacing={1.5} mt={2}>
                                    {[
                                        ['Total Product Sales', `$${amazonOverview.totalProductSales?.toFixed(2)}`],
                                        ['Total Product Charges', `$${amazonOverview.totalProductCharges?.toFixed(2)}`],
                                        ['Total Net Sales', `$${amazonOverview.totalNetSales?.toFixed(2)}`],
                                        ['Total Refunds', `$${amazonOverview.totalRefunds?.toFixed(2)}`],
                                        ['Return %', `${amazonOverview.returnPercentage?.toFixed(2)}%`],
                                        ['Marketplace Charges', `$${amazonOverview.marketplaceCharges?.toFixed(2)}`],
                                    ].map(([label, val], i) => (
                                        <Stack key={i} direction="row" justifyContent="space-between">
                                            <Typography variant="body1">{label}:</Typography>
                                            <Typography variant="body1"><b>{val}</b></Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* VePay Overview */}
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h5" color="primary" gutterBottom><b>VePay Overview</b></Typography>
                                <Stack spacing={1.5} mt={2}>
                                    {[
                                        ['Total Funded Amount', `$${vepayOverview.totalFundedAmount?.toFixed(2)}`],
                                        ['Receivable Balance', `$${vepayOverview.receivableBalance?.toFixed(2)}`],
                                        ['VePay Fee %', `${vepayOverview.vepayFeePercentage?.toFixed(2)}%`],
                                        ['VePay Fee', `$${vepayOverview.vepayFee?.toFixed(2)}`],
                                    ].map(([label, val], i) => (
                                        <Stack key={i} direction="row" justifyContent="space-between">
                                            <Typography variant="body1">{label}:</Typography>
                                            <Typography variant="body1"><b>{val}</b></Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </SectionBox>

            {/* ═══════════════════════ Sales Analysis Section ═════════════════ */}
            <SectionBox>
                <SectionTitle>SALES ANALYSIS</SectionTitle>

                {/* Sales Analysis Column Chart */}
                <HighchartsReact highcharts={Highcharts} options={buildColumnOptions(
                    '', salesAnalysisData.dates,
                    [{ name: 'Total Sales', data: salesAnalysisData.totalSales }, { name: 'Product Sales', data: salesAnalysisData.productSales }]
                )} />

                {/* Top Selling Products Table */}
                <SubSectionTitle>Top Selling Products</SubSectionTitle>
                <StyledTableContainer sx={{ mt: 1 }}>
                    <Table sx={{ minWidth: 650 }} aria-label="top selling products">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F6F6F6" }}>
                                <StyledTableCellHeader align="left">Description</StyledTableCellHeader>
                                <StyledTableCellHeader align="right">Total Product Sales</StyledTableCellHeader>
                                <StyledTableCellHeader align="right">Total Net Sales</StyledTableCellHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {topProducts.length > 0 ? topProducts.map((row, i) => (
                                <TableRow hover key={i}>
                                    <StyledTableCellBody align="left">{row.description}</StyledTableCellBody>
                                    <StyledTableCellBody align="right">{"$"}{row.totalProductSales?.toFixed(2)}</StyledTableCellBody>
                                    <StyledTableCellBody align="right">{"$"}{row.totalNetSales?.toFixed(2)}</StyledTableCellBody>
                                </TableRow>
                            )) : <EmptyRow colSpan={3} message="No products found" />}
                        </TableBody>
                    </Table>
                </StyledTableContainer>

                {/* Top Sales by SKU - Pie | Top Sales by State - Donut */}
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={6}>
                        <SubSectionTitle>Top Sales by SKU</SubSectionTitle>
                        <HighchartsReact highcharts={Highcharts} options={buildPieOptions('Top Sales by SKU', topSalesBySku)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <SubSectionTitle>Top Sales by State</SubSectionTitle>
                        <HighchartsReact highcharts={Highcharts} options={buildDonutOptions('Top Sales by State', topSalesByState)} />
                    </Grid>
                </Grid>
            </SectionBox>

            {/* ═══════════════════════ Return Analysis Section ════════════════ */}
            <SectionBox>
                <SectionTitle>RETURN ANALYSIS</SectionTitle>

                {/* Return Analysis Column Chart */}
                <HighchartsReact highcharts={Highcharts} options={buildColumnOptions(
                    '', returnAnalysisData.dates,
                    [{ name: 'Total Net Sales', data: returnAnalysisData.totalNetSales }, { name: 'Total Refunds', data: returnAnalysisData.totalRefunds, color: '#e74c3c' }]
                )} />

                {/* Top Returns by Product Table */}
                <SubSectionTitle>Top Returns by Product</SubSectionTitle>
                <StyledTableContainer sx={{ mt: 1 }}>
                    <Table sx={{ minWidth: 650 }} aria-label="top returns by product">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F6F6F6" }}>
                                <StyledTableCellHeader align="left">Description</StyledTableCellHeader>
                                <StyledTableCellHeader align="right">Total Refunds</StyledTableCellHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {topReturnsByProduct.length > 0 ? topReturnsByProduct.map((row, i) => (
                                <TableRow hover key={i}>
                                    <StyledTableCellBody align="left">{row.description}</StyledTableCellBody>
                                    <StyledTableCellBody align="right">{"$"}{row.totalRefunds?.toFixed(2)}</StyledTableCellBody>
                                </TableRow>
                            )) : <EmptyRow colSpan={2} message="No returns found" />}
                        </TableBody>
                    </Table>
                </StyledTableContainer>

                {/* Top Returns by SKU - Pie | Top Returns by State - Donut */}
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={6}>
                        <SubSectionTitle>Top Returns by SKU</SubSectionTitle>
                        <HighchartsReact highcharts={Highcharts} options={buildPieOptions('Top Returns by SKU', topReturnsBySku, ['#e74c3c', '#c0392b', '#f39c12', '#e67e22', '#d35400', '#e74c3c', '#c0392b', '#f39c12'])} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <SubSectionTitle>Top Returns by State</SubSectionTitle>
                        <HighchartsReact highcharts={Highcharts} options={buildDonutOptions('Top Returns by State', topReturnsByState, ['#e74c3c', '#c0392b', '#f39c12', '#e67e22', '#d35400', '#e74c3c', '#c0392b', '#f39c12'])} />
                    </Grid>
                </Grid>
            </SectionBox>

            {/* ═══════════════════════ Amazon Charges Analysis ════════════════ */}
            <SectionBox>
                <SectionTitle>AMAZON CHARGES ANALYSIS</SectionTitle>

                {/* Column: Total Net Sales vs Total Amazon Charges */}
                <HighchartsReact highcharts={Highcharts} options={buildColumnOptions(
                    '', amazonChargesAnalysis.dates,
                    [{ name: 'Total Net Sales', data: amazonChargesAnalysis.totalNetSales }, { name: 'Total Amazon Charges', data: amazonChargesAnalysis.totalAmazonCharges, color: '#e74c3c' }]
                )} />

                {/* Total Amazon Charges Breakdown - Pie */}
                <SubSectionTitle>Total Amazon Charges Breakdown</SubSectionTitle>
                <HighchartsReact highcharts={Highcharts} options={buildPieOptions('Amazon Charges Breakdown', amazonChargesBreakdown)} />

                {/* Product Charges on Sales - Table */}
                <SubSectionTitle>Product Charges on Sales</SubSectionTitle>
                <StyledTableContainer sx={{ mt: 1 }}>
                    <Table sx={{ minWidth: 650 }} aria-label="product charges on sales">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#F6F6F6" }}>
                                <StyledTableCellHeader align="left">Description</StyledTableCellHeader>
                                <StyledTableCellHeader align="right">Total Product Sales</StyledTableCellHeader>
                                <StyledTableCellHeader align="right">Total Charges</StyledTableCellHeader>
                                <StyledTableCellHeader align="right">Charges %</StyledTableCellHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {productChargesOnSales.length > 0 ? productChargesOnSales.map((row, i) => (
                                <TableRow hover key={i}>
                                    <StyledTableCellBody align="left">{row.description}</StyledTableCellBody>
                                    <StyledTableCellBody align="right">{"$"}{row.totalProductSales?.toFixed(2)}</StyledTableCellBody>
                                    <StyledTableCellBody align="right">{"$"}{row.totalCharges?.toFixed(2)}</StyledTableCellBody>
                                    <StyledTableCellBody align="right">{row.chargesPercent?.toFixed(2)}%</StyledTableCellBody>
                                </TableRow>
                            )) : <EmptyRow colSpan={4} message="No charge data found" />}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </SectionBox>

            {/* ═══════════════════════ VePay Funding Analysis ═════════════════ */}
            <SectionBox>
                <SectionTitle>VEPAY FUNDING ANALYSIS</SectionTitle>
                <HighchartsReact highcharts={Highcharts} options={buildColumnOptions(
                    '', vepayFundingData.dates,
                    [{ name: 'Funded', data: vepayFundingData.funded }, { name: 'VePay Fee', data: vepayFundingData.vepayFee, color: '#f39c12' }]
                )} />
            </SectionBox>

            {/* ═══════════════════════ Monthly Summary ════════════════════════ */}
            <SectionBox>
                <SectionTitle>MONTHLY SUMMARY</SectionTitle>

                {/* Monthly Product Sales vs Monthly Net Sales - Column */}
                <HighchartsReact highcharts={Highcharts} options={buildColumnOptions(
                    '', monthlySummary.months,
                    [{ name: 'Monthly Product Sales', data: monthlySummary.productSales }, { name: 'Monthly Net Sales', data: monthlySummary.netSales }]
                )} />

                {/* Totals Cards */}
                <Grid container spacing={3} sx={{ mt: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h5" color="primary" gutterBottom><b>Total Monthly Promotional Rebates</b></Typography>
                                <Typography variant="h4" sx={{ mt: 2 }}><b>{"$"}{totalMonthlyPromoRebates.toFixed(2)}</b></Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h5" color="primary" gutterBottom><b>Total Monthly FBA Fees</b></Typography>
                                <Typography variant="h4" sx={{ mt: 2 }}><b>{"$"}{totalMonthlyFbaFees.toFixed(2)}</b></Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </SectionBox>
        </div>
    );
}
